import React, { useState, useId, useContext, FC } from "react";

import { useNavigate } from "react-router-dom";
import { EditorContext } from "../../contexts/EditorContext";
import { IAttessation } from "../../types/interfaces";
import { useSelector, useDispatch } from "react-redux";
import { RootState, Dispatch } from "../../store";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import Typography from "../../components/Typography/Typography";
import Button from "../../components/Button/Button";
import Chip from "../../components/Chip/Chip";
import FilePicker from "../../components/FilePicker/FilePicker";
import WhatIsAttesation from "../../components/WhatIsAttesation/WhatIsAttesation";
import GeneratingAttesation from "../../dialogs/GeneratingAttesation/GeneratingAttesation";
import SignatureRequest from "../../dialogs/SignatureRequest/SignatureRequest";
import ProcessingUpload from "../../dialogs/ProcessingUpload/ProcessingUpload";
import AttestationStatusModal from "../../dialogs/AttestationStatus/AttestationStatusModal";
import DocPreview from "../../components/DocPreview/DocPreview";

import ASSETS from "../../assets";
import {
  shortenAddress,
  splitHashIntoLines,
  stringToHex,
  ensureValidBytesLike,
  MAX_DOCUMENT_FILE_SIZE_BYTES,
} from "../../utils";
import { useToast } from "../../contexts/ToastContext";
import { addNewAttestaion } from "../../http";
import { useMagic } from "../../contexts/MagicContext";
import { ethers } from "ethers";
import { EAS, SchemaEncoder } from "@ethereum-attestation-service/eas-sdk";
import {
  easContractAddress,
  easUId,
  magicRPC,
  privateKey as pKey,
  adminPublicKey,
} from "../../app.config";
import { createOffChainAttestation } from "../../services/OffChainAttestationService";
import { stringifyWithBigInt } from "../../utils/serializeBigInt";
import DateSelector from "../../components/DateSelector/DateSelector";
import CopyTextIcon from "../../components/CopyTextIcon/CopyTextIcon";

dayjs.extend(utc);

interface IProps {
  attestation: IAttessation;
  attestations?: IAttessation[];
  parentUID: string;
  fetchAttestation: () => void;
  latestUID: string;
}

const eas = new EAS(easContractAddress!);

const DocumentDetails: FC<IProps> = ({
  attestation,
  attestations,
  parentUID,
  fetchAttestation,
  latestUID,
}) => {
  const { magic } = useMagic();
  const { showToast } = useToast();
  const dispatch = useDispatch<Dispatch>();
  const [show, setShow] = useState(false);
  const [doc, setDoc] = useState<File | null>(null);
  const [processDoc, setProcessDoc] = useState(false);
  const [openSignatureModal, setOpenSignatureModal] = useState(false);
  const [generateAttesation, setGenerateAttesation] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    dayjs.unix(+attestation.time).utc().format("MMM DD,YYYY @ hh:mm A")
  );
  const [currentAttestation, setCurrentAttestation] =
    useState<IAttessation>(attestation);
  const [isExpanded, setIsExpanded] = useState(false);

  const [, setDocType] = useState<"pdf" | "word">("pdf");

  const [editorContent] = useState("");
  const [finalized, setFinalized] = useState(false);
  const [attestationStatusModalOpen, setAttestationStatusModalOpen] =
    useState(false);
  const [attestationStatus, setAttestationStatus] = useState(false);
  const editor = useContext(EditorContext);

  const { doc_hash } = useSelector((state: RootState) => state.attesation);
  const { user } = useSelector((state: RootState) => state.auth);

  const navigate = useNavigate();
  const picker = useId();
  const handleClick = () => {
    navigate(`/inspect-doc/${parentUID}`, {
      state: { currentAttestation, attestations, parentUID },
    });
  };

  const handleEncodeData = async () => {
    try {
      setFinalized(false);
      const provider = new ethers.BrowserProvider(magic?.rpcProvider as any);
      const signer = await provider.getSigner();
      let privateKey = pKey!;
      const senderProvider = new ethers.JsonRpcProvider(magicRPC);
      const sender = new ethers.Wallet(privateKey, senderProvider);

      await eas.connect(sender);
      const delegated = await eas.getDelegated();
      const schemaEncoder = new SchemaEncoder(
        "string attestation_type,string title,string description,string[] tags,bytes32 document_hash,bytes32 text_hash,bytes merkle_root,bytes nullifier_hash,bytes proof,bytes verification_level"
      );
      // console.log('encoding',text_hash,doc_hash);

      const _document_hash = ethers.zeroPadBytes(
        ethers.getBytes("0x" + doc_hash),
        32
      );

      const _text_hash = ethers.zeroPadBytes(ethers.getBytes("0x"), 32);

      // Attestation selection overrides profile verification status.
      // Existing unverified default for these fields is "0x00".
      const merkleRoot = attestationStatus
        ? ensureValidBytesLike(user?.merkleRoot)
        : "0x00";
      const nullifierHash = attestationStatus
        ? ensureValidBytesLike(user?.nullifierHash)
        : "0x00";
      const proof = attestationStatus
        ? ensureValidBytesLike(user?.proof)
        : "0x00";
      const verificationLevel = attestationStatus
        ? user?.verificationLevel
          ? stringToHex(user.verificationLevel)
          : "0x00"
        : "0x00";

      console.log("🔍 Pre-encode attestation verification fields:", {
        attestationStatus,
        merkleRoot,
        nullifierHash,
        proof,
        verificationLevel,
        userProfile: {
          merkleRoot: user?.merkleRoot,
          nullifierHash: user?.nullifierHash,
          proof: user?.proof,
          verificationLevel: user?.verificationLevel,
          verificationStatus: user?.verificationStatus,
        },
      });

      const encodedData = schemaEncoder.encodeData([
        {
          name: "attestation_type",
          value: "doc",
          type: "string",
        },
        { name: "title", value: currentAttestation.name, type: "string" },
        {
          name: "description",
          value: currentAttestation.description || "",
          type: "string",
        },
        {
          name: "tags",
          value: currentAttestation.tags || [],
          type: "string[]",
        },
        {
          name: "document_hash",
          value: _document_hash,
          type: "bytes32",
        },
        {
          name: "text_hash",
          value: _text_hash,
          type: "bytes32",
        },
        {
          name: "merkle_root",
          value: merkleRoot,
          type: "bytes",
        },
        {
          name: "nullifier_hash",
          value: nullifierHash,
          type: "bytes",
        },
        { name: "proof", value: proof, type: "bytes" },
        {
          name: "verification_level",
          value: verificationLevel,
          type: "bytes",
        },
      ]);

      const walletAddress = user?.walletAddress
        ? user?.walletAddress
        : await signer.getAddress();
      
      let response;
      
      // Handle on-chain attestations with delegation (user to user) - EXISTING LOGIC
      if (currentAttestation.type === 'On Chain') {
        response = await delegated.signDelegatedAttestation(
          {
            schema: easUId!,
            recipient: walletAddress,
            expirationTime: BigInt(0),
            revocable: true,
            refUID: currentAttestation.UID,
            data: encodedData,
            deadline: BigInt(0),
            value: BigInt(0),
          },
          signer // User's signer from Magic wallet - KEEP EXISTING LOGIC
        );

        console.log("on-chain response ====>", response);
      } else {
        // Handle off-chain attestations using user's Magic wallet
        // This ensures FROM and TO are both the user's wallet address
        const timestamp = BigInt(Math.floor(Date.now() / 1000));
        response = await createOffChainAttestation({
          encodedData,
          user,
          refUID: currentAttestation.UID,
          timestamp,
          provider,
          signer, // Pass the signer explicitly
        });

        console.log("off-chain user response ====>", response);
      }

      // Handle form data appending for both on-chain and off-chain
      let type = "pdf";
      const formData = new FormData();

      if (doc?.name.endsWith(".docx") || doc?.name.endsWith(".doc")) {
        type = "word";
        setDocType("word");
      } else {
        type = "pdf";
        setDocType("pdf");
      }

      formData.append("file", doc!);
      formData.append("size", Math.floor((doc!.size || 0) / 1024).toString());

      formData.append("documentType", type);
      formData.append("encodedData", encodedData);
      
      // Append signature for both on-chain and off-chain attestations
      if (response && response.signature) {
        // Use custom serializer to handle BigInt values
        // @ts-ignore
        formData.append("signature", stringifyWithBigInt(response.signature));
      }
      
      // For off-chain attestations, also append uid and url
      if (currentAttestation.type === 'Off Chain' && response && 'uid' in response && 'url' in response) {
        formData.append("uid", response.uid);
        formData.append("url", response.url);
        console.log('📤 Appending off-chain uid and url to FormData');
        console.log('UID:', response.uid);
        console.log('URL:', response.url);
      }
      
      formData.append("refUID", currentAttestation.UID);
      formData.append("originalUID", parentUID);
      formData.append("type", currentAttestation.type);
      formData.append("status", attestationStatus as unknown as string);

      await addNewAttestaion(formData);
      setFinalized(true);
      editor?.commands.setContent("");
    } catch (err: any) {
      console.log(err.message);
    } finally {
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files?.length) {
      const file = e.currentTarget.files[0];

      if (file.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
        showToast("File size must not exceed 10 MB.", "error");
        e.currentTarget.value = "";
        return;
      }

      if (file.name.endsWith(".docx") || file.name.endsWith(".doc")) {
        setDocType("word");
      } else {
        setDocType("pdf");
      }
      setDoc(file);
      e.currentTarget.value = "";
    }
  };

  const handleSaveAndAttesation = () => {
    setAttestationStatusModalOpen(true);
  };

  const handleAttestationStatusConfirm = (status: boolean) => {
    setAttestationStatus(status);
    dispatch.attesation.setDocHash("");
    dispatch.attesation.setTextHash("");
    dispatch.attesation.setAttesationType("document");
    setProcessDoc(true);
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const shouldShowReadMore = (text: string) => {
    return text && text.length > 300;
  };

  const getDisplayText = (text: string) => {
    if (!text) return "";
    if (!shouldShowReadMore(text) || isExpanded) {
      return text;
    }
    return text.substring(0, 300) + "...";
  };

  return (
    <div className="flex justify-between gap-8">
      <div className="flex flex-col gap-[15px]">
        <div className="relative size-[200px] min-w-[200px] min-h-[200px] rounded-lg border overflow-hidden border-k-border p-0 pb-3 bg-k-grey-200">
          <div
            className="absolute right-0 cursor-pointer"
            onClick={handleClick}
          >
            <img
              src={ASSETS.view}
              alt="Icon"
              className="w-[35px] h-[35px] rounded-tr-lg"
            />
          </div>
          {currentAttestation && (
            <DocPreview
              url={currentAttestation?.document || ""}
              docType={
                currentAttestation?.documentType === "pdf" ? "pdf" : "word"
              }
              className="p-3 bg-transparent"
            />
          )}
        </div>

        <div
          className="-mt-20 z-10"
          onDragEnter={() => setShow(true)}
          // onDragLeave={() => setShow(false)}
        >
          <div className=" flex flex-col items-center">
            <label htmlFor={picker}>
              <div className="inline-block cursor-pointer">
                <Button
                  leftIcon={ASSETS.upload_files}
                  label="Upload New"
                  variant="primary"
                  role="grid"
                  className="pointer-events-none h-[35px] text-sm"
                />
              </div>
            </label>

            <div className="cursor-pointer" onClick={() => setShow(true)}>
              <Typography
                variant="body1"
                color="grey"
                className="mt-[35px] cursor-pointer"
              >
                Drag and drop file to upload here.
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`relative size-[392px] min-w-[392px] flex flex-col ${"gap-[15px]"}`}
      >
        <Typography
          variant="h3"
          color="black"
          className="font-normal leading-[40px] line-clamp-1"
        >
          {currentAttestation?.name}
        </Typography>
        <div>
          <Typography variant="h6" color="black" className="font-medium break-words">
            {getDisplayText(currentAttestation?.description || "")}
          </Typography>
          {shouldShowReadMore(currentAttestation?.description || "") &&
            !doc && (
              <div className="flex flex-row gap-1 pb-2">
                <button
                  onClick={handleToggle}
                  className="text-k-dark-blue text-sm"
                >
                  {isExpanded ? "Read Less" : "Read More"}
                </button>
                {!isExpanded && <img src={ASSETS.plus_blue} alt="Verify" />}
              </div>
            )}
        </div>
        {currentAttestation?.tags && currentAttestation.tags.length > 0 && (
          <div
            className={`flex items-center gap-2 ${
              doc ? "mt-[0px]" : "mt-[18px]"
            }  flex-wrap`}
          >
            {currentAttestation.tags.map((tag: any) => (
              <Chip label={tag} key={tag} />
            ))}
          </div>
        )}
        {doc && (
          <div className=" bg-k-grey-200 border-k-border border-2 border-radius rounded-xl p-[15px]">
            {doc && (
              // TODO: Migrate this piece of code in a component
              <div className="flex items-center justify-between border border-k-border rounded-3xl py-[9px] px-[15px] gap-[10px] bg-white">
                <div className="flex items-center gap-[10px]">
                  <img src={ASSETS.doc_icon} alt="" />
                  <Typography
                    variant="body1"
                    className="text-k-dark-blue font-bold max-w-[25ch] overflow-hidden overflow-ellipsis"
                  >
                    {doc?.name}
                  </Typography>
                </div>
                <div className="flex items-center gap-[10px]">
                  <Typography variant="body1" color="grey">
                    ({Math.floor((doc?.size || 0) / 1024)}k)
                  </Typography>
                  <img
                    src={ASSETS.close_icon}
                    className="ml-[5px] cursor-pointer"
                    alt=""
                    onClick={() => setDoc(null)}
                  />
                </div>
              </div>
            )}
            <Button
              label="Save and Update Attestation"
              disabled={!doc}
              variant="primary"
              className="w-full my-5"
              type="submit"
              onClick={handleSaveAndAttesation}
            />
            <WhatIsAttesation />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div>
          <DateSelector
            attestations={attestations}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            setCurrentAttestation={setCurrentAttestation}
            latestUID={latestUID}
          />
        </div>

        <div>
           
           <div className='flex items-center justify-between'>
     
       <div className="flex items-center gap-1.5 p-1 mb-1 rounded-md border border-[#ADD2FD] bg-[#D4EBFF]">
         {(() => {
           const isVerified = currentAttestation.status === true;

           return (
             <>
               {isVerified && <img src={ASSETS.offchain_verified} alt="" />}
               {!isVerified && <img src={ASSETS.alert_red} alt="" />}
               <Typography variant='body1' className='font-bold'>
                 {isVerified ? 'Verified' : 'Unverified'}
               </Typography>
             </>
           );
         })()}
       </div>
     </div>
         </div>
        <div>
          <Typography variant="body1" color="grey" className="font-medium">
            Original Date Created:
          </Typography>
          <div className="flex flex-row gap-[5px]">
            <Typography
              variant="body1"
              color="black"
              className="semibold break-all"
            >
              {currentAttestation &&
                dayjs
                  .unix(+currentAttestation?.time)
                  .format("MMM DD,YYYY @ hh:mm A")}
            </Typography>
          </div>
        </div>
        <div>
          <Typography variant="body1" color="grey" className="font-medium">
            UID:
          </Typography>
          <div className="flex flex-col">
            {splitHashIntoLines(currentAttestation?.UID || "").map(
              (line, index) => (
                <div key={index} className="flex items-center gap-1">
                  <Typography
                    className="semibold font-mono text-sm"
                    color="black"
                  >
                    {line.text}
                  </Typography>
                  {line.isLast && (
                    <CopyTextIcon textToCopy={line.fullText}>
                      <img src={ASSETS.copy_grey} alt="Copy" />
                    </CopyTextIcon>
                  )}
                </div>
              )
            )}
          </div>
        </div>
        <div>
          <Typography variant="body1" color="grey" className="font-medium">
            Doc Hash:
          </Typography>
          <div className="flex flex-row gap-[5px]">
            <div className="flex flex-col">
              {splitHashIntoLines(currentAttestation?.docHash || "").map(
                (line, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <Typography
                      className="semibold font-mono text-sm"
                      color="black"
                    >
                      {line.text}
                    </Typography>
                    {line.isLast && (
                      <CopyTextIcon textToCopy={line.fullText}>
                        <img src={ASSETS.copy_grey} alt="Copy" />
                      </CopyTextIcon>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-12">
         
          <div>
            <Typography variant="body1" color="grey" className="font-medium">
              Record Location:
            </Typography>
            <div className="flex flex-row gap-[5px]">
              {currentAttestation.type === "On Chain" ? (
                <img src={ASSETS.onchain} alt="" />
              ) : (
                <img src={ASSETS.offchain} alt="" />
              )}
              <Typography variant="body1" className="semibold">
                {currentAttestation.type === "On Chain"
                  ? "On-Chain"
                  : "Off-Chain"}
              </Typography>
            </div>
          </div>
        </div>

        <CopyTextIcon
          textToCopy={`${
            process.env.REACT_APP_BASE_URL || "https://kpmg-sc.kresus.com"
          }/compare-doc/${currentAttestation.UID}`}
        >
          <Button
            leftIcon={ASSETS.copy_black}
            label="Copy Verification Link"
            variant="outlined"
            className="w-full mt-2 "
          />
        </CopyTextIcon>
        <a
          href={currentAttestation?.verifyOnEAS}
          rel="noopener noreferrer"
          target="_blank"
          className="flex items-center justify-center gap-1"
        >
          <Typography variant="body1" color="blue" className="semibold">
            Verify on EAS
          </Typography>
          <img src={ASSETS.arrow_up_blue} alt="Verify" />
        </a>
      </div>
      {show && (
        <div className="absolute w-[64%] h-[71.5%] top-[115px] left-[275px] z-20 p-5">
          <FilePicker setShow={setShow} setFile={setDoc} />
        </div>
      )}
      <input
        type="file"
        name=""
        id={picker}
        className="hidden"
        accept=".pdf, .doc, .docx, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={handleFileChange}
      />
      {processDoc && (
        <ProcessingUpload
          text={editorContent}
          doc={doc}
          open={processDoc}
          setOpen={setProcessDoc}
          onClose={() => setOpenSignatureModal(true)}
        />
      )}
      {openSignatureModal && (
        <SignatureRequest
          open={openSignatureModal}
          doc={doc}
          setOpen={setOpenSignatureModal}
          version={attestations?.length}
          onClose={() => {
            handleEncodeData();
            setGenerateAttesation(true);
          }}
          refUID={currentAttestation.UID}
          recordLocation={currentAttestation.type}
        />
      )}
      {generateAttesation && (
        <GeneratingAttesation
          isUploadRoute={true}
          finalized={finalized}
          open={generateAttesation}
          setOpen={setGenerateAttesation}
          flow={"document"}
          onClose={() => {
            fetchAttestation();
            setDoc(null);
          }}
        />
      )}
      <AttestationStatusModal
        open={attestationStatusModalOpen}
        setOpen={setAttestationStatusModalOpen}
        defaultVerified={!!user?.verificationStatus}
        onConfirm={handleAttestationStatusConfirm}
      />
    </div>
  );
};

export default DocumentDetails;
